import type { ThemeTokens } from "../types";
import {
  BLACK,
  WHITE,
  contrastBetween,
  contrastRatio,
  mix,
  parseHex,
  toHex,
} from "./contrast";

/**
 * One accent colour in, a coherent ramp out. Mirrors how the Tymbo app derives a booking
 * page theme from a single brand colour: pick the on-accent text by measured contrast
 * rather than by guessing light or dark.
 */
export function deriveAccent(base: ThemeTokens, accent: string, dark: boolean): ThemeTokens {
  const rgb = parseHex(accent);
  if (!rgb) return base;

  const onWhite = contrastRatio(rgb, WHITE);
  const onBlack = contrastRatio(rgb, BLACK);
  const onAccent = onWhite >= onBlack ? "#ffffff" : "#141414";

  // A tinted surface for chips and toggle tracks: toward white on light, toward the
  // page background on dark, so it reads as a tint rather than a second accent.
  const accentSoft = dark ? toHex(mix(rgb, BLACK, 0.72)) : toHex(mix(rgb, WHITE, 0.85));

  return {
    ...base,
    accent: toHex(rgb),
    accentSoft,
    onAccent,
    // Keeping the secondary button identical to the primary is what makes
    // "Accept all" and "Reject all" provably equivalent.
    secondaryBg: toHex(rgb),
    secondaryText: onAccent,
  };
}

interface ContrastCheck {
  label: string;
  foreground: keyof ThemeTokens;
  background: keyof ThemeTokens;
  minimum: number;
}

const CHECKS: ContrastCheck[] = [
  { label: "body text", foreground: "text", background: "bg", minimum: 4.5 },
  { label: "muted text", foreground: "textMuted", background: "bg", minimum: 4.5 },
  { label: "primary button", foreground: "onAccent", background: "accent", minimum: 4.5 },
  { label: "secondary button", foreground: "secondaryText", background: "secondaryBg", minimum: 4.5 },
];

/**
 * Dev-only. A consent dialog is part of the service under the European Accessibility Act,
 * so 4.5:1 is a legal floor rather than a nicety — worth failing loudly in development.
 */
export function warnOnPoorContrast(tokens: ThemeTokens): void {
  for (const check of CHECKS) {
    const ratio = contrastBetween(tokens[check.foreground], tokens[check.background]);
    if (ratio === null || ratio >= check.minimum) continue;
    console.warn(
      `[consentkit] ${check.label} contrast is ${ratio.toFixed(2)}:1 ` +
        `(${tokens[check.foreground]} on ${tokens[check.background]}), below the ${check.minimum}:1 ` +
        "required by WCAG 2.1 AA / EN 301 549.",
    );
  }

  if (tokens.accent !== tokens.secondaryBg) {
    console.warn(
      "[consentkit] the accept and reject buttons no longer share a background. " +
        "German DPA guidance requires both first-layer options to be visually equivalent — " +
        "differing colours are treated as nudging.",
    );
  }
}
